import {
  LabelConfig,
  LabelElement,
  TextElement,
  BarcodeElement,
  QRCodeElement,
  DataMatrixElement,
  GS1DataMatrixElement,
  GS1Code128Element,
  LineElement,
  RectangleElement,
  CircleElement,
  ImageElement,
  VariableData,
} from '@/types'
import { mmToDots } from '@/lib/utils/units'
import { encodeGS1ForZPL, encodeGS1ForZPLCode128 } from '@/lib/gs1/gs1Encoder'

// Cache for converted images (to avoid re-converting on each ZPL generation)
const imageCache = new Map<string, { hexData: string; bytesPerRow: number; totalBytes: number; width: number; height: number }>()

export function generateZPL(
  label: LabelConfig,
  elements: LabelElement[],
  variableData?: VariableData
): string {
  const lines: string[] = []

  // Start label format
  lines.push('^XA')

  // Print settings (if configured)
  if (label.printSettings) {
    // Print orientation: ^PO<orientation>
    // N = Normal, I = Inverted (180° rotation)
    // For landscape, the label itself is physically rotated, not the ZPL orientation
    // Zebra printers use ^PO for 180° flip, not for portrait/landscape
    // Portrait/Landscape is handled by physical label orientation
    if (label.printSettings.orientation === 'landscape') {
      // For landscape, we could rotate the content but typically
      // the physical label is rotated in the printer
      // ^PON = Normal, ^POI = Inverted (upside down)
      lines.push('^PON')
    }

    // Print rate (speed): ^PR<slew>,<backfeed>,<media>
    // Speed is in inches/second
    lines.push(`^PR${label.printSettings.printSpeed},${label.printSettings.printSpeed},${label.printSettings.printSpeed}`)

    // Set darkness: ~SD<darkness>
    // Value is 0-30 (relative darkness)
    lines.push(`~SD${label.printSettings.darkness.toString().padStart(2, '0')}`)

    // Media tracking: ^MN<type>
    // N = Web (gap) sensing, M = Black mark sensing, Y = Continuous
    switch (label.printSettings.mediaType) {
      case 'gap':
        lines.push('^MNW') // Web (gap) sensing - standard transmissive
        break
      case 'mark':
        lines.push('^MNM') // Black mark sensing
        break
      case 'continuous':
        lines.push('^MNY') // Continuous media
        break
    }
  }

  // Set print width
  const printWidth = mmToDots(label.width, label.dpi)
  lines.push(`^PW${printWidth}`)

  // Set label length
  const labelLength = mmToDots(label.height, label.dpi)
  lines.push(`^LL${labelLength}`)

  // Set label home (offset from margins if any)
  if (label.margins && (label.margins.left > 0 || label.margins.top > 0)) {
    const homeX = mmToDots(label.margins.left, label.dpi)
    const homeY = mmToDots(label.margins.top, label.dpi)
    lines.push(`^LH${homeX},${homeY}`)
  }

  // Generate ZPL for each element
  for (const element of elements) {
    const elementZPL = generateElementZPL(element, label.dpi, variableData)
    if (elementZPL) {
      lines.push(elementZPL)
    }
  }

  // End label format
  lines.push('^XZ')

  return lines.join('\n')
}

function generateElementZPL(
  element: LabelElement,
  dpi: 203 | 300,
  variableData?: VariableData
): string {
  const x = mmToDots(element.position.x, dpi)
  const y = mmToDots(element.position.y, dpi)

  switch (element.type) {
    case 'text':
      return generateTextZPL(element, x, y, dpi, variableData)
    case 'barcode-code128':
    case 'barcode-code39':
    case 'barcode-ean13':
    case 'barcode-ean8':
    case 'barcode-upca':
    case 'barcode-upce':
    case 'barcode-itf':
      return generateBarcodeZPL(element, x, y, dpi, variableData)
    case 'gs1-code128':
      return generateGS1Code128ZPL(element, x, y, dpi, variableData)
    case 'qrcode':
      return generateQRCodeZPL(element, x, y, variableData)
    case 'datamatrix':
      return generateDataMatrixZPL(element, x, y, variableData)
    case 'gs1-datamatrix':
      return generateGS1DataMatrixZPL(element, x, y, variableData)
    case 'line':
      return generateLineZPL(element, x, y, dpi)
    case 'rectangle':
      return generateRectangleZPL(element, x, y, dpi)
    case 'circle':
      return generateCircleZPL(element, x, y, dpi)
    case 'image':
      return generateImageZPL(element, x, y, dpi)
    default:
      return ''
  }
}

// Escape special ZPL characters in text content using hex encoding
function escapeZPLText(text: string): string {
  // In ZPL with ^FH enabled, use _XX format for special characters
  // The caret (^) and tilde (~) are command prefixes and must be escaped
  let result = ''
  for (const char of text) {
    const code = char.charCodeAt(0)
    // Escape caret, tilde, backslash, and underscore (since underscore is our hex prefix)
    if (char === '^' || char === '~' || char === '\\' || char === '_') {
      result += `_${code.toString(16).toUpperCase().padStart(2, '0')}`
    } else {
      result += char
    }
  }
  return result
}

function generateTextZPL(
  element: TextElement,
  x: number,
  y: number,
  dpi: 203 | 300,
  variableData?: VariableData
): string {
  const lines: string[] = []

  // Field origin
  lines.push(`^FO${x},${y}`)

  // Field reverse (white on black)
  if (element.reverse) {
    lines.push('^FR')
  }

  // Font selection: ^A<font><orientation>,<height>,<width>
  lines.push(`^A${element.font}${element.rotation},${element.fontSize},${element.fontSize}`)

  // Field data
  let content = element.content
  if (element.isVariable && element.variableName && variableData) {
    content = variableData[element.variableName] || content
  }

  // Use ^FN for variable fields if no data provided
  if (element.isVariable && element.variableName && !variableData) {
    lines.push(`^FN1^FD${element.variableName}^FS`)
  } else {
    // Enable hex mode (^FH) for proper character encoding
    lines.push(`^FH^FD${escapeZPLText(content)}^FS`)
  }

  return lines.join('')
}

function generateBarcodeZPL(
  element: BarcodeElement,
  x: number,
  y: number,
  dpi: 203 | 300,
  variableData?: VariableData
): string {
  const lines: string[] = []

  // Field origin
  lines.push(`^FO${x},${y}`)

  // Field reverse (white on black)
  if (element.reverse) {
    lines.push('^FR')
  }

  // Bar code field default
  lines.push(`^BY${element.moduleWidth}`)

  // Barcode height in dots
  const height = mmToDots(element.height, dpi)

  // Get content
  let content = element.content
  if (element.isVariable && element.variableName && variableData) {
    content = variableData[element.variableName] || content
  }

  // Barcode command based on type
  const printText = element.showText ? 'Y' : 'N'

  switch (element.type) {
    case 'barcode-code128':
      // ^BC<orientation>,<height>,<print interpretation line>,<print interpretation line above code>,<UCC check digit>,<mode>
      lines.push(`^BC${element.rotation},${height},${printText},N,N,N`)
      break
    case 'barcode-code39':
      // ^B3<orientation>,<mod-43 check digit>,<height>,<print interpretation line>,<print interpretation line above code>
      lines.push(`^B3${element.rotation},N,${height},${printText},N`)
      break
    case 'barcode-ean13':
      // ^BE<orientation>,<height>,<print interpretation line>,<print interpretation line above code>
      lines.push(`^BE${element.rotation},${height},${printText},N`)
      break
    case 'barcode-ean8':
      // ^B8<orientation>,<height>,<print interpretation line>,<print interpretation line above code>
      lines.push(`^B8${element.rotation},${height},${printText},N`)
      break
    case 'barcode-upca':
      // ^BU<orientation>,<height>,<print interpretation line>,<print interpretation line above code>,<print check digit>
      lines.push(`^BU${element.rotation},${height},${printText},N,Y`)
      break
    case 'barcode-upce':
      // ^B9<orientation>,<height>,<print interpretation line>,<print interpretation line above code>,<print check digit>
      lines.push(`^B9${element.rotation},${height},${printText},N,Y`)
      break
    case 'barcode-itf':
      // ^B2<orientation>,<height>,<print interpretation line>,<print interpretation line above code>,<calculate and print Mod 10 check digit>
      lines.push(`^B2${element.rotation},${height},${printText},N,N`)
      break
  }

  // Field data
  lines.push(`^FD${content}^FS`)

  return lines.join('')
}

function generateQRCodeZPL(
  element: QRCodeElement,
  x: number,
  y: number,
  variableData?: VariableData
): string {
  const lines: string[] = []

  // Field origin
  lines.push(`^FO${x},${y}`)

  // Field reverse (white on black)
  if (element.reverse) {
    lines.push('^FR')
  }

  // QR Code: ^BQ<orientation>,<model>,<magnification factor>,<error correction>,<mask value>
  // Model 2 is standard, magnification 1-10, error correction H/Q/M/L
  lines.push(`^BQ${element.rotation},2,${element.magnification},${element.errorCorrection},7`)

  // Get content
  let content = element.content
  if (element.isVariable && element.variableName && variableData) {
    content = variableData[element.variableName] || content
  }

  // Field data - QR requires special format with data type indicator
  // Using automatic data mode (A for alphanumeric, M for mixed)
  lines.push(`^FDMA,${content}^FS`)

  return lines.join('')
}

function generateDataMatrixZPL(
  element: DataMatrixElement,
  x: number,
  y: number,
  variableData?: VariableData
): string {
  const lines: string[] = []

  // Field origin
  lines.push(`^FO${x},${y}`)

  // Field reverse (white on black)
  if (element.reverse) {
    lines.push('^FR')
  }

  // DataMatrix: ^BX<orientation>,<height>,<quality level>,<columns>,<rows>,<format ID>,<escape character>,<aspect ratio>
  lines.push(`^BX${element.rotation},${element.size},200`)

  // Get content
  let content = element.content
  if (element.isVariable && element.variableName && variableData) {
    content = variableData[element.variableName] || content
  }

  // Field data
  lines.push(`^FD${content}^FS`)

  return lines.join('')
}

function generateGS1DataMatrixZPL(
  element: GS1DataMatrixElement,
  x: number,
  y: number,
  variableData?: VariableData
): string {
  const lines: string[] = []

  // Field origin
  lines.push(`^FO${x},${y}`)

  // Field reverse (white on black)
  if (element.reverse) {
    lines.push('^FR')
  }

  // DataMatrix: ^BX<orientation>,<height>,<quality level>,<columns>,<rows>,<format ID>,<escape character>,<aspect ratio>
  // For GS1 DataMatrix, we use format ID 6 which enables automatic FNC1 insertion
  // The escape character _ (underscore) allows us to use _1 for FNC1 and _d029 for GS
  lines.push(`^BX${element.rotation},${element.size},200,,,6,_`)

  // Encode GS1 data with FNC1 and separators
  // The data starts with _1 (FNC1) followed by AI+value pairs
  // Variable-length AIs are separated by _d029 (GS character, ASCII 29)
  const gs1Data = encodeGS1ForZPL(element.applicationIdentifiers, variableData)

  // Field data
  lines.push(`^FD${gs1Data}^FS`)

  return lines.join('')
}

function generateGS1Code128ZPL(
  element: GS1Code128Element,
  x: number,
  y: number,
  dpi: 203 | 300,
  variableData?: VariableData
): string {
  const lines: string[] = []

  // Field origin
  lines.push(`^FO${x},${y}`)

  // Field reverse (white on black)
  if (element.reverse) {
    lines.push('^FR')
  }

  // Bar code field default (module width)
  lines.push(`^BY${element.moduleWidth}`)

  // Barcode height in dots
  const height = mmToDots(element.height, dpi)

  // Encode GS1 data with FNC1 for Code 128
  // Format: >8 = FNC1, data follows
  const gs1Data = encodeGS1ForZPLCode128(element.applicationIdentifiers, variableData)

  // Code 128: ^BC<orientation>,<height>,<print interpretation line>,<print interpretation line above code>,<UCC check digit>,<mode>
  // For GS1-128, we use mode D (automatic mode selection) and UCC check digit Y
  const printText = element.showText ? 'Y' : 'N'
  lines.push(`^BC${element.rotation},${height},${printText},N,Y,D`)

  // Field data - starts with >8 for FNC1 (already included in gs1Data)
  lines.push(`^FD${gs1Data}^FS`)

  return lines.join('')
}

function generateLineZPL(
  element: LineElement,
  x: number,
  y: number,
  dpi: 203 | 300
): string {
  // In our model:
  // - width = length of the line (the long dimension)
  // - height = thickness of the line (the thin dimension)
  const lineLength = mmToDots(element.width, dpi)
  const lineThickness = Math.max(mmToDots(element.height, dpi), 1)

  // ^GB<width>,<height>,<thickness>,<color>,<rounding>
  // For rotation R (90°) or B (270°), we create a vertical line
  // Horizontal line: ^GB{length},{thickness},{thickness},B
  // Vertical line (90°): ^GB{thickness},{length},{thickness},B
  const isRotated90 = element.rotation === 'R' || element.rotation === 'B'

  if (isRotated90) {
    // Vertical line: narrow width, tall height
    return `^FO${x},${y}^GB${lineThickness},${lineLength},${lineThickness},${element.color}^FS`
  } else {
    // Horizontal line: wide width, narrow height
    return `^FO${x},${y}^GB${lineLength},${lineThickness},${lineThickness},${element.color}^FS`
  }
}

function generateRectangleZPL(
  element: RectangleElement,
  x: number,
  y: number,
  dpi: 203 | 300
): string {
  const width = mmToDots(element.width, dpi)
  const height = mmToDots(element.height, dpi)

  if (element.filled) {
    // Filled rectangle - use thickness equal to half the smaller dimension
    const thickness = Math.min(width, height)
    return `^FO${x},${y}^GB${width},${height},${thickness},${element.color}^FS`
  } else {
    // Hollow rectangle
    return `^FO${x},${y}^GB${width},${height},${element.borderThickness},${element.color}^FS`
  }
}

function generateCircleZPL(
  element: CircleElement,
  x: number,
  y: number,
  dpi: 203 | 300
): string {
  const diameter = mmToDots(element.diameter, dpi)

  // ^GC<diameter>,<thickness>,<color>
  return `^FO${x},${y}^GC${diameter},${element.borderThickness},${element.color}^FS`
}

function generateImageZPL(
  element: ImageElement,
  x: number,
  y: number,
  dpi: 203 | 300
): string {
  // Check cache first
  const cacheKey = `${element.id}-${element.width}-${element.height}-${dpi}`
  let cachedData = imageCache.get(cacheKey)

  if (!cachedData) {
    // Convert image synchronously for ZPL generation
    // This is done by pre-converting when the image is added
    const converted = convertImageToGrfSync(element.imageData, element.width, element.height, dpi)
    if (converted) {
      cachedData = converted
      imageCache.set(cacheKey, cachedData)
    }
  }

  if (!cachedData) {
    return '' // Skip if conversion failed
  }

  // ^GFA = Graphic Field, format A (ASCII hex)
  // Parameters: total bytes, total bytes, bytes per row, data
  return `^FO${x},${y}^GFA,${cachedData.totalBytes},${cachedData.totalBytes},${cachedData.bytesPerRow},${cachedData.hexData}^FS`
}

/**
 * Synchronously converts an image to ZPL GRF format
 * Uses a temporary canvas to process the image
 */
function convertImageToGrfSync(
  imageData: string,
  widthMm: number,
  heightMm: number,
  dpi: 203 | 300
): { hexData: string; bytesPerRow: number; totalBytes: number; width: number; height: number } | null {
  try {
    // Create an offscreen canvas
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    // Calculate target size in dots
    const dotsPerMm = dpi / 25.4
    const targetWidth = Math.round(widthMm * dotsPerMm)
    const targetHeight = Math.round(heightMm * dotsPerMm)

    canvas.width = targetWidth
    canvas.height = targetHeight

    // Create image element
    const img = new Image()
    img.src = imageData

    // If image is already loaded (from cache/base64), process it
    if (img.complete && img.naturalWidth > 0) {
      return processImageToGrf(ctx, img, targetWidth, targetHeight)
    }

    // For async images, we need to pre-load - return placeholder for now
    // The actual conversion happens when the image is added via the dialog
    return null
  } catch {
    return null
  }
}

function processImageToGrf(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  targetWidth: number,
  targetHeight: number
): { hexData: string; bytesPerRow: number; totalBytes: number; width: number; height: number } {
  const canvas = ctx.canvas

  // Fill with white background
  ctx.fillStyle = 'white'
  ctx.fillRect(0, 0, targetWidth, targetHeight)

  // Draw image scaled to target size
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

  // Get image data
  const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight)
  const pixels = imageData.data

  // Convert to monochrome and then to hex
  const bytesPerRow = Math.ceil(targetWidth / 8)
  const totalBytes = bytesPerRow * targetHeight
  const threshold = 128

  let hexData = ''

  for (let y = 0; y < targetHeight; y++) {
    for (let byteIndex = 0; byteIndex < bytesPerRow; byteIndex++) {
      let byte = 0

      for (let bit = 0; bit < 8; bit++) {
        const x = byteIndex * 8 + bit

        if (x < targetWidth) {
          const pixelIndex = (y * targetWidth + x) * 4
          const r = pixels[pixelIndex]
          const g = pixels[pixelIndex + 1]
          const b = pixels[pixelIndex + 2]

          // Convert to grayscale
          const gray = 0.299 * r + 0.587 * g + 0.114 * b

          // If darker than threshold, it's black (1)
          if (gray < threshold) {
            byte |= (1 << (7 - bit))
          }
        }
      }

      // Convert byte to 2-character hex
      hexData += byte.toString(16).toUpperCase().padStart(2, '0')
    }
  }

  return {
    width: targetWidth,
    height: targetHeight,
    bytesPerRow,
    totalBytes,
    hexData
  }
}

/**
 * Pre-convert image when it's added to the canvas
 * This ensures the image is ready for ZPL generation
 */
export function preConvertImageForZpl(
  element: ImageElement,
  dpi: 203 | 300
): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (ctx) {
        const dotsPerMm = dpi / 25.4
        const targetWidth = Math.round(element.width * dotsPerMm)
        const targetHeight = Math.round(element.height * dotsPerMm)

        canvas.width = targetWidth
        canvas.height = targetHeight

        const grfData = processImageToGrf(ctx, img, targetWidth, targetHeight)
        const cacheKey = `${element.id}-${element.width}-${element.height}-${dpi}`
        imageCache.set(cacheKey, grfData)
      }

      resolve()
    }

    img.onerror = () => resolve()
    img.src = element.imageData
  })
}
